import { Dialog } from '@/components/atoms/Dialog';
import { Input } from '@/components/atoms/Input';
import Loader, { Status } from '@/components/Loader/Loader';
import { cn } from '@/lib/classNames';
import { reportGetReportsRequest } from '@/modules/report/reportActions';
import type { User } from '@/modules/user/userTypes';
import {
	CheckmarkGreenCircleFilled,
	ExclamationMarkRed
} from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChangeEvent, ReactNode, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { ReportUserPeriod } from '../../@types/types';
import { useReportStatusData } from '../../hooks/useReportStatusData';
import { useUserData } from '../../hooks/useUserData';
import { useUsersData } from '../../hooks/useUsersData';

type MultiUsersActivityModalProps = {
	children: ReactNode;
	reportUserPeriod: ReportUserPeriod;
};

export const MultiUsersActivityModal = ({
	children,
	reportUserPeriod
}: MultiUsersActivityModalProps) => {
	const { user } = useUserData();
	const { users } = useUsersData();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedEmails, setSelectedEmails] = useState<Array<string>>([]);

	const sortedUsers = useMemo(
		() =>
			users.sort(({ email: email1 }, { email: email2 }) => {
				const sortEmail1 = email1.toLowerCase();
				const sortEmail2 = email2.toLowerCase();

				if (sortEmail1 < sortEmail2) {
					return -1;
				}
				if (sortEmail1 > sortEmail2) {
					return 1;
				}

				return 0;
			}),
		[users]
	);

	const dispatch = useDispatch();

	const handleDeselectUsers = () => {
		setSelectedEmails([]);
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User unselected all in multiple user activity report`
		});
	};

	const handleSelectAllUsers = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User pressed 'Select All Users' in multiple user activity report`,
				properties: {
					numUsers: users.length
				}
			});
		}

		setSelectedEmails([...new Set(users.map(({ email }) => email))]);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handleSelectUser = (value: string) => {
		setSelectedEmails(state => {
			const stateSet = new Set(state);

			if (stateSet.has(value)) {
				stateSet.delete(value);

				return [...stateSet];
			}

			stateSet.add(value);
			return [...stateSet];
		});
	};

	const handleSendReport = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: `User pulled multiple user activity report`,
				properties: {
					numUsers: selectedEmails.length,
					users: selectedEmails,
					startDate: reportUserPeriod.startDate,
					endDate: reportUserPeriod.endDate
				}
			});
		}

		dispatch(
			reportGetReportsRequest({
				...reportUserPeriod,
				userEmail: selectedEmails
			})
		);

		handleCloseModal();
	};

	return (
		<Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
			<Dialog.Trigger asChild>{children}</Dialog.Trigger>

			<Dialog.Content>
				<Dialog.Header>
					<Dialog.Title>Select Users from below</Dialog.Title>

					<Dialog.Description>
						Click &apos;Send My Report&apos; to receive a CSV
						activity report covering all selected <br /> users
						(select at least 2). The report will be delivered to
						your email within a few minutes.
					</Dialog.Description>
				</Dialog.Header>

				<div className="flex flex-1 flex-col overflow-hidden rounded-md bg-dm-black3.5">
					<button
						type="button"
						onClick={handleDeselectUsers}
						data-is-visible={selectedEmails.length > 0}
						className={cn(
							'flex h-full w-full flex-1 cursor-pointer gap-2 bg-lightest-grey p-2 text-dm-black duration-200 fill-mode-forwards',
							'data-[is-visible="true"]:animate-in data-[is-visible="true"]:fade-in data-[is-visible="true"]:zoom-in',
							'data-[is-visible="false"]:hidden'
						)}
					>
						<Input
							size="sm"
							type="checkbox"
							rounded="sm"
							checked
							readOnly
						/>
						<span className="sr-only">Deselect users</span>

						<p>{selectedEmails.length} selected</p>
					</button>

					<MultiUsersActivityList
						isOpen={isModalOpen}
						itemsList={sortedUsers}
						selectedEmails={selectedEmails}
						onSelectUser={handleSelectUser}
					/>
				</div>

				<Dialog.Footer className="gap-4">
					<button
						type="button"
						onClick={handleSelectAllUsers}
						className="text-orange"
					>
						Select All Users
					</button>

					<SendReportButton onSendReport={handleSendReport} />
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};

type MultiUsersActivityListProps = {
	isOpen: boolean;
	itemsList: Array<User>;
	selectedEmails: Array<string>;
	onSelectUser: (value: string) => void;
};

const MultiUsersActivityList = ({
	isOpen,
	itemsList,
	selectedEmails,
	onSelectUser
}: MultiUsersActivityListProps) => {
	const parentRef = useRef(null);

	const rowVirtualizer = useVirtualizer({
		count: isOpen ? itemsList.length : 0,
		debug: true,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 35,
		overscan: 10
	});

	const virtualizedList = rowVirtualizer.getVirtualItems();

	const handleSelectUser = (event: ChangeEvent<HTMLInputElement>) => {
		onSelectUser(event.target.value);
	};

	return (
		<div
			ref={parentRef}
			className="flex max-h-80 flex-col gap-3 overflow-hidden overflow-y-auto p-2"
		>
			{isOpen && (
				<ul
					className="relative"
					style={{
						height: `${rowVirtualizer.getTotalSize()}px`
					}}
				>
					{virtualizedList.map(virtualRow => {
						const { id, email } = itemsList[virtualRow.index];

						return (
							<li key={id + email}>
								<label className="flex cursor-pointer items-center gap-2 rounded-sm p-2 text-lightest-grey transition-colors hover:bg-dm-black hover:text-lightest-grey">
									<Input
										size="sm"
										type="checkbox"
										rounded="sm"
										name="selected-email"
										value={email}
										onChange={handleSelectUser}
										checked={selectedEmails.includes(email)}
									/>

									<p>{email}</p>
								</label>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

type SendReportButtonProps = {
	onSendReport: () => void;
};

const SendReportButton = ({ onSendReport }: SendReportButtonProps) => {
	const { reportStatus, reportIsLoading } = useReportStatusData();

	if (reportIsLoading) {
		return (
			<Loader
				className="send-report-button-loading"
				status={Status.LOADING}
			/>
		);
	}

	if (reportStatus === 'success') {
		return (
			<CheckmarkGreenCircleFilled className="send-report-button-success" />
		);
	}

	if (reportStatus === 'failure') {
		return <ExclamationMarkRed className="send-report-button-failure" />;
	}

	return (
		<button type="button" onClick={onSendReport} className="text-orange">
			Send my report
		</button>
	);
};
